<template>
  <div class="flex items-center justify-center w-full">
    <!-- Dropzone -->
    <div v-if="pages.length === 0" @click="fileInput.click()" @dragover.prevent="isDragging = true"   @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" class="w-full max-w-4xl bg-gradient-to-b from-green-50 to-white hover:from-white hover:to-green-50 mt-5 transition-all duration-300 cursor-pointer shadow-lg rounded-2xl flex flex-col items-center justify-center text-center py-8 px-4 md:py-16 md:px-8 relative overflow-hidden border-2" :class="[ isDragging  ? 'border-[#24bd64ea] bg-green-100 shadow-2xl' : 'border-[#2ecc71]']">
      <!-- Loading indicator -->
      <div v-if="isProcessing" class="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-50 rounded-2xl">
          <div class="animate-spin rounded-full h-16 w-16 border-4 border-[#e5e7eb] border-t-[#2ecc71] mb-4"></div>
          <p class="text-black font-semibold text-lg">Processing {{ totalPagesBeingProcessed }} pages...</p>
          <p class="text-gray-600 text-sm mt-2">This may take a moment</p>
      </div>
      <div class="relative z-10 flex flex-col items-center">
          <div class="mx-auto w-20 h-20 flex items-center justify-center mb-4">
            <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/file-0pDKRBmhgCFNU0B1MBrmzaqeKqum8d.png" alt="PDF Upload" class="w-full h-full object-contain" />
          </div>
            <!-- Upload Button -->
          <button @click.stop="fileInput.click()" class="bg-[#2ecc71] text-white font-semibold px-8 py-3 rounded-lg active:bg-[#24bd64ea] 
          transition-all duration-200 flex items-center gap-2 shadow-md">
          <Upload :size="20" />
          <span class="font-semibold">Select Files</span>
          </button>
          <p class="mb-5 font-semibold text-base text-black mt-6">
          Drag and Drop PDFs here
          </p>
          <p class="text-sm text-gray-700 font-medium">
          or press <span class="bg-[#e5e7eb] px-2 py-1 rounded font-mono text-black">CTRL + V</span> to paste
          </p>
      </div>
    </div>
      <!-- Main Editor -->
      <div v-else class="flex flex-col lg:flex-row w-full gap-4 lg:gap-8 px-2 md:px-0 pb-20">
          <aside class="w-full lg:w-[30%] bg-gray-50 mt-1 p-3 md:p-4 shadow-md border-t-4 border-green-400 rounded-md lg:sticky lg:top-24 h-fit z-20">
              <!-- Signature block -->
              <div class="mb-4">
                <div>
                  <!-- Signature Box -->
                  <div
                    class="bg-white border border-gray-200 rounded-xl p-3 md:p-4 
                          hover:border-green-400 hover:shadow-md transition-all duration-300
                          cursor-pointer relative"
                    :class="{ 'cursor-grab': signatureImage, 'cursor-not-allowed': !signatureImage }"
                    @click="openSignModal"
                    draggable="true"
                    @dragstart="handleDragStart"
                    @dragend="handleDragEnd"
                  >
                    <!-- Title -->
                    <p class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      ✍️ Your Signature
                    </p>

                    <!-- Signature Image -->
                    <div
                      v-if="signatureImage"
                      class="flex items-center justify-center py-3"
                    >
                      <img
                        :src="signatureImage"
                        class="w-[160px] md:w-[200px] h-[60px] md:h-[70px] object-contain pointer-events-none drop-shadow-sm"
                        alt="Signature"
                      />
                    </div>

                    <!-- Placeholder State -->
                    <div
                      v-else
                      class="h-14 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 border border-dashed border-gray-300"
                    >
                      <img
                        src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/download-aAo7Y7ChvS2Yb4TH8tROwPYzT8qh1O.png"
                        class="h-10 opacity-70"
                      />
                    </div>

                    <!-- Drag hint -->
                    <p
                      v-if="signatureImage"
                      class="text-xs text-green-600 mt-3 text-center font-medium animate-pulse"
                    >
                      Drag & drop to PDF
                    </p>
                  </div>
                </div>
              </div>
              <div class="max-w-full">
                <div class="grid grid-cols-2 gap-2 md:gap-3">
                    <!-- Feature Card -->
                    <div
                      v-for="(feature, index) in features"
                      :key="index"
                      class="group relative overflow-hidden rounded-xl bg-white border border-gray-200 transition-all duration-300 hover:shadow-lg hover:border-green-300 opacity-60"
                    >
                      <!-- Gradient Overlay on Hover -->
                      <div class="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <!-- Content -->
                      <div class="relative p-2 md:p-3 flex flex-col h-full">
                        <!-- Icon Container -->
                        <div class="w-5 h-5 rounded-lg bg-gradient-to-br from-green-50 to-green-100/50 flex items-center justify-center mb-2 md:mb-4 group-hover:scale-105 transition-transform duration-300">
                          <component
                            :is="feature.icon"
                            class="w-4 h-4 md:w-5 md:h-5 text-green-600"
                          />
                        </div>

                        <!-- Title -->
                        <h3 class="text-[10px] md:text-xs font-semibold text-gray-900 mb-1 md:mb-2 truncate">
                          {{ feature.title }}
                        </h3>

                        <!-- Progress Bar & Quarter -->
                        <div class="flex items-center gap-2 md:gap-3 pt-2 md:pt-4 border-t border-gray-100">
                          <div class="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              class="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                              :style="{ width: feature.progress + '%' }"
                            />
                          </div>
                        </div>

                        <!-- Coming Soon Badge -->
                        <div class="absolute top-0.5 right-1 md:right-3">
                          <span class="inline-flex items-center gap-1.5 px-1 py-1 rounded-md bg-green-100 text-green-700 text-[6px] md:text-[8px] font-semibold">
                            <span class="w-1 h-1 ml-0.5 rounded-full bg-green-500 animate-pulse" />
                            Soon
                          </span>
                        </div>
                      </div>
                    </div>
                </div>
              </div>
          </aside>
          <div class="w-full lg:w-[70%] flex items-start justify-center">
              <div class="w-full max-w-7xl">
                  <div class="mx-auto">
                      <!-- Toolbar -->
                      <div class="bg-white rounded-md shadow-md p-3 md:p-4 border-b border-green-100 border-t-4 border-green-400 top-16 md:top-20 z-30 transition-all">
                        <div class="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <p class="text-gray-800 text-lg md:text-xl w-full sm:w-auto text-center sm:text-left">
                                <span v-if="fileInfo.uniqueFiles.length">
                                  Editing :
                                <span class="font-semibold block sm:inline truncate max-w-[200px] sm:max-w-[300px] mx-auto sm:mx-0">
                                    {{
                                      fileInfo.fileTextDisplay.length > 30
                                        ? fileInfo.fileTextDisplay.slice(0, 30) + '...'
                                        : fileInfo.fileTextDisplay
                                    }}
                                  </span>
                                </span>
                            </p>
                            
                            <div class="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                              <button @click="reloadPage" class="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 md:px-4 rounded-lg flex gap-2 font-semibold transition shadow-sm text-sm md:text-base flex-1 sm:flex-initial justify-center">
                              <RotateCcw :size="18" class="mt-0.5" />
                              Restart
                              </button>
                              <button
                              @click="saveAndDownload"
                              :disabled="isDownloading"
                              class="bg-[#2ecc71] hover:bg-[#24bd64ea] disabled:opacity-70 disabled:cursor-not-allowed 
                                      text-white px-3 py-2 md:px-4 rounded-md flex gap-2 font-semibold transition text-sm md:text-base shadow-sm hover:shadow-md flex-1 sm:flex-initial justify-center">
                              <!-- Loader -->
                              <span v-if="isDownloading" class="flex items-center gap-2">
                                  <svg class="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                  <path class="opacity-75" fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z">
                                  </path>
                                  </svg>
                                  Processing...
                              </span>

                              <!-- Normal Button -->
                              <span v-else class="flex items-center gap-2">
                                  <Download :size="20" class="mt-0.5" />
                                  Download PDF
                              </span>
                              </button>
                            </div>
                        </div>
                      </div>
                      <!-- Processing pages message -->
                      <div v-if="isProcessing" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center gap-3">
                        <div class="animate-spin rounded-full h-5 w-5 border-2 border-green-400 border-t-green-600"></div>
                        <p class="text-green-700 font-medium">Processing {{ totalPagesBeingProcessed }} pages... it will appear shortly</p>
                      </div>
                      <div class="mt-3" 
                        :style="pages.length > 15 ? `max-height: ${maxHeight}px` : ''">
                        <!-- Pages Grid -->
                        <div class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4 mb-8">
                          <div 
                            v-for="element in pages" 
                            :key="element.uid"
                            class="transition-all relative z-0"
                            :class="[element.isLoading ? 'opacity-75' : '']"
                          >
                            <!-- Loading overlay -->
                            <div v-if="element.isLoading" class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center z-20 rounded-lg">
                              <div class="flex flex-col items-center gap-2">
                                <div class="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-green-400"></div>
                                <p class="text-white text-xs font-semibold">Loading...</p>
                              </div>
                            </div>
                            <!-- PDF Page with Drop Zone -->
                            <div 
                              class="flex items-center justify-center p-0.5 relative z-0 w-full max-w-4xl mx-auto"
                              @drop="handleSignatureDrop($event, element.uid)"
                              @dragover.prevent
                              @dragenter.prevent="element.isDragOver = true"
                              @dragleave.prevent="element.isDragOver = false"
                              :class="{ 'ring-4 ring-green-400': element.isDragOver }"
                            >
                              <img :src="element.thumbnail" class="max-w-full max-h-full object-contain shadow-2xl pointer-events-none" />
                              
                              <!-- Placed Signatures on this page -->
                              <div 
                                v-for="sig in getSignaturesForPage(element.uid)" 
                                :key="sig.id"
                                class="absolute cursor-move group hover:outline hover:outline-2 hover:outline-dashed hover:outline-blue-400 hover:outline-offset-2"
                                :style="{
                                  left: sig.x + '%',
                                  top: sig.y + '%',
                                  width: sig.width + '%',
                                  height: sig.height + '%'
                                }"
                                @mousedown.prevent="startDragSignature($event, sig)"
                              >
                                <img :src="sig.image" class="w-full h-full object-contain pointer-events-none" />
                                
                                <!-- Edit button - Top Right (first) -->
                                <button
                                  @click.stop="openSignModal"
                                  class="absolute -top-4 right-6 bg-gray-200 hover:bg-gray-300 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10"
                                  title="Edit Signature"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="gray" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                                    <path d="m15 5 4 4"/>
                                  </svg>
                                </button>
                                
                                <!-- Delete button - Top Right (second) -->
                                <button
                                  @click.stop="deleteSignature(sig.id)"
                                  class="absolute -top-4 -right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
                                  title="Delete">
                                  <X :size="14" />
                                </button>
                                
                                <!-- Resize handle - Bottom Right -->
                                <div
                                  @mousedown.stop.prevent="startResize($event, sig)"
                                  class="absolute -bottom-3 -right-3 w-5 h-5 bg-blue-500 rounded cursor-se-resize opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                                  title="Resize"
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    <!-- Signature Modal -->
    <div v-if="showSignModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] pointer-events-auto p-3">
      <!-- Close button - Fixed to screen top-right -->
      <button 
        @click="showSignModal = false" 
        class="fixed top-6 right-6 bg-white hover:bg-gray-100 text-gray-700 hover:text-black rounded-full p-2 shadow-lg transition-all
         duration-200 z-[10001] border-2 border-gray-200 hover:border-gray-300"
        title="Close">
        <X :size="24" />
      </button>
      
      <!-- Modal Content -->
      <div class="bg-white w-auto max-w-6xl rounded-xl shadow-2xl relative z-[10000]">
        <SignPdf :isEmbedded="true" :existingSignature="signatureData" @signed="handleSigned" />
      </div>
    </div>
    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      multiple
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import SignPdf from './SignPdf.vue'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import { Upload, RotateCcw, RotateCw, Trash2, Download, X, Text, Image, Video, Table, DollarSign, ListOrdered, Scissors, Type } from 'lucide-vue-next'

const isDownloading = ref(false)

// Props
const props = defineProps({
  initialSignature: {
    type: [String, Object],
    default: null
  }
})

// Set worker for pdfjs
pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const showSignModal = ref(false)
const signatureData = ref(props.initialSignature)

function openSignModal() {
  showSignModal.value = true
}

function handleSigned(signatureDataUrl) {
  // Store signature data
  signatureData.value = signatureDataUrl
  // Close modal
  showSignModal.value = false
  console.log('Signature saved:', signatureDataUrl)
}

const features = ref([
  {
    title: 'Text Formatting',
    icon: Text,
  },
  {
    title: 'Image Gallery',
    icon: Image,
  },
  {
    title: 'Video Integration',
    icon: Video,
  },
  {
    title: 'Data Tables',
    icon: Table,
  },
  {
    title: 'Page Breaks',
    icon: Scissors,
  },
  {
    title: 'Form Fields',
    icon: Type,
  }
])

// Computed property to get image from signature data
const signatureImage = computed(() => {
  if (!signatureData.value) return null
  // If it's an object, return the image property
  if (typeof signatureData.value === 'object') {
    return signatureData.value.image
  }
  // If it's a string, return it directly (old format)
  return signatureData.value
})

// Watch for signature changes and update all placed signatures
watch(signatureImage, (newImage) => {
  if (newImage && placedSignatures.value.length > 0) {
    // Update all placed signatures with new image
    placedSignatures.value.forEach(sig => {
      sig.image = newImage
    })
    console.log('Updated all placed signatures with new signature')
  }
})

// Emits
const emit = defineEmits(['back-to-sign', 'pdf-uploaded'])

// Refs
const pages = ref([])
const pdfjsDocs = ref({})
const pdfLibDocs = ref({})
const previewPages = ref([])
const isDragging = ref(false)
const isProcessing = ref(false)
const totalPagesBeingProcessed = ref(0)
let uidCounter = 0
const fileInput = ref(null)
const previewCanvas = ref(null)

// Signature placement system
const placedSignatures = ref([])
let signatureIdCounter = 0
const isDraggingSignature = ref(false)
const isResizing = ref(false)
let currentDragSignature = null
let dragStartX = 0
let dragStartY = 0
let resizeStartWidth = 0
let resizeStartHeight = 0

const fileInfo = computed(() => {
  const uniqueFiles = [...new Set(pages.value.map(p => p.fileName))]
  const fileCount = uniqueFiles.length

  // Page plural
  const pageText = pages.value.length === 1 ? 'Page' : 'Pages'

  // File text logic:
  let fileTextDisplay = ''

  if (fileCount <= 2) {
    // 1 ya 2 files → names show
    fileTextDisplay = uniqueFiles.join(', ')
  } else {
    // 3+ files → sirf count show
    fileTextDisplay = `${fileCount} Files`
  }

  return { uniqueFiles, fileCount, fileTextDisplay, pageText }
})

const handleDrop = (e) => {
  isDragging.value = false
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFiles([file])
        break
      }
    }
  }
}

const processFiles = async (files) => {
  const pdfFiles = files.filter(f => f.type === 'application/pdf')
  if (!pdfFiles.length) {
    alert('Please select PDF files only')
    return
  }

  isProcessing.value = true

  for (const file of pdfFiles) {
    try {
      const buffer = await file.arrayBuffer()

      // Load with pdf-lib
      const pdfLibDoc = await PDFDocument.load(buffer)
      pdfLibDocs.value[file.name] = pdfLibDoc

      // Load with pdfjs
      const pdfjsDoc = await pdfjsLib.getDocument({ data: buffer }).promise
      pdfjsDocs.value[file.name] = pdfjsDoc

      totalPagesBeingProcessed.value = pdfjsDoc.numPages

      // Generate thumbnails with loading state
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        pages.value.push({
          uid: uidCounter++,
          fileName: file.name,
          pageNum: i,
          rotation: 0,
          thumbnail: '',
          isLoading: true
        })
      }

      // Render pages sequentially
      let pageIndex = pages.value.length - pdfjsDoc.numPages
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const { thumbnail } = await renderPage(pdfjsDoc, i, 0)
        if (pages.value[pageIndex]) {
          pages.value[pageIndex].thumbnail = thumbnail
          pages.value[pageIndex].isLoading = false
        }
        pageIndex++
      }
    } catch (error) {
      console.error('Error processing PDF:', error)
      alert('Error processing PDF file. Please try again.')
    }
  }

  isProcessing.value = false
  totalPagesBeingProcessed.value = 0
  
  // Emit event that PDF has been uploaded
  if (pages.value.length > 0) {
    emit('pdf-uploaded')
  }
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const renderPage = async (pdfDoc, pageNum, rotation = 0) => {
  const page = await pdfDoc.getPage(pageNum);

  const viewport = page.getViewport({
    scale: 2.5,
    rotation,
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;

  return { thumbnail: canvas.toDataURL("image/png") };
};


const toggleSelect = (uid) => {
  const idx = selectedUids.value.indexOf(uid)
  if (idx === -1) selectedUids.value.push(uid)
  else selectedUids.value.splice(idx, 1)
}

const renderPreviewPage = async (index) => {
  if (!previewPages.value[index]) return

  await nextTick()

  const canvas = previewCanvas.value
  if (!canvas) {
  console.error('Preview canvas not found')
    return
  }

  const page = previewPages.value[index]
  const pdfjsDoc = pdfjsDocs.value[page.fileName]
  if (!pdfjsDoc) {
    console.error('PDF document not loaded for preview')
    return
  }

  try {
    const pdfPage = await pdfjsDoc.getPage(page.pageNum)

    // Get container size for responsive scaling
    const container = canvas.parentElement
    const maxWidth = container.clientWidth * 0.9
    const maxHeight = container.clientHeight * 0.9

    // Get original viewport
    let viewport = pdfPage.getViewport({ scale: 1, rotation: page.rotation })
    let scale = Math.min(maxWidth / viewport.width, maxHeight / viewport.height, 3) // Max scale 3
    scale = Math.max(scale, 0.5) // Min scale

    viewport = pdfPage.getViewport({ scale, rotation: page.rotation })

    // Set canvas size
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Use requestAnimationFrame to ensure rendering after resize
    requestAnimationFrame(async () => {
      try {
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        }
        await pdfPage.render(renderContext).promise
      } catch (err) {
        console.error('Render failed:', err)
      }
    })
  } catch (error) {
    console.error('Error rendering preview:', error)
  }
}

const createMergedPdf = async () => {
  // 1. Create a fresh PDF and copy all pages FIRST (without modification)
  const mergedPdf = await PDFDocument.create()
  const pageUidMap = [] // To map new page index -> pageUid

  for (const pageInfo of pages.value) {
    const sourcePdfDoc = pdfLibDocs.value[pageInfo.fileName]
    if (!sourcePdfDoc) continue

    // Use copyPages (preserves vectors/text) but don't modify yet
    const [copiedPage] = await mergedPdf.copyPages(sourcePdfDoc, [pageInfo.pageNum - 1])
    mergedPdf.addPage(copiedPage)
    pageUidMap.push(pageInfo.uid)
  }

  // 2. "Sanitize" the PDF by saving and reloading
  // This fixes the 'Expected instance of PDFArray2' error by flattening internal structures
  // before we attempt to modify the pages (add signatures).
  const intermediateBytes = await mergedPdf.save()
  const finalPdf = await PDFDocument.load(intermediateBytes)
  
  // 3. Draw Signatures on the clean PDF
  const finalPages = finalPdf.getPages()
  
  for (let i = 0; i < finalPages.length; i++) {
    const newPage = finalPages[i]
    const pageUid = pageUidMap[i]
    
    // Get signatures for this specific page
    const signaturesForPage = placedSignatures.value.filter(sig => sig.pageUid === pageUid)

    for (const sig of signaturesForPage) {
      try {
        let imageData = sig.image
        if (typeof imageData === 'string' && imageData.includes('base64,')) {
          imageData = imageData.split('base64,')[1]
        }

        const bytes = Uint8Array.from(atob(imageData), c => c.charCodeAt(0))
        let embeddedSig

        try {
          embeddedSig = await finalPdf.embedPng(bytes)
        } catch (pngError) {
          try {
            embeddedSig = await finalPdf.embedJpg(bytes)
          } catch (jpgError) {
            console.error('Signature embed failed:', jpgError)
            continue
          }
        }

        const { width, height } = newPage.getSize()
        
        // Calculate dimensions from percentage
        // If stored as %, we convert to points based on Page Width/Height
        const layoutW = (sig.width / 100) * width
        const layoutH = (sig.height / 100) * height
        
        // Convert Position
        const x = (sig.x / 100) * width
        
        // Start Y from top: (sig.y / 100) * height is distance from top
        // PDF Y 0 is bottom
        // so Bottom Y = height - distTop - objectHeight
        const y = height - (sig.y / 100) * height - layoutH

        newPage.drawImage(embeddedSig, {
          x,
          y,
          width: layoutW,
          height: layoutH,
        })
      } catch (err) {
        console.error('Error drawing signature:', err)
      }
    }
  }

  return await finalPdf.save()
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true   // Loader ON

    await nextTick()             // UI freeze na ho
    await new Promise(resolve => setTimeout(resolve, 50)) // Device hang se bachao

    const bytes = await createMergedPdf()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `organized-${Date.now()}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
  } finally {
    isDownloading.value = false  // Loader OFF
  }
}

const reloadPage = () => {
  window.location.reload()
}

// Calculate maxHeight dynamically based on 15 items
const maxHeight = computed(() => {
  // Estimate item height including padding/margin
  const itemHeight = 228 // 8rem = 128px
  const gap = 16 // Tailwind gap-4 = 1rem = 16px

  // Determine number of columns based on Tailwind breakpoints
  const width = window.innerWidth
  let columns = 2
  if (width >= 640) columns = 3
  if (width >= 768) columns = 4
  if (width >= 1024) columns = 5
  if (width >= 1280) columns = 5

  const rowsFor15Items = Math.ceil(15 / columns)
  return rowsFor15Items * itemHeight + (rowsFor15Items - 1) * gap
})

// Signature Drag-Drop Functions
function handleDragStart(e) {
  if (!signatureImage.value) {
    e.preventDefault()
    return
  }
  e.dataTransfer.effectAllowed = 'copy'
  e.dataTransfer.setData('text/plain', 'signature')
}

function handleDragEnd() {
  // Cleanup after drag
  isDropping = false
}

let isDropping = false

function handleSignatureDrop(e, pageUid) {
  e.preventDefault()
  e.stopPropagation()
  
  if (!signatureImage.value || isDropping) return
  
  isDropping = true
  
  // Use currentTarget to ensure we get the container bounds (which should match the image due to w-fit)
  const rect = e.currentTarget.getBoundingClientRect()
  
  // Calculate position as percentage
  // We clamp to ensure it stays roughly inside, but allow some bleeding edge if needed
  const xPx = e.clientX - rect.left
  const yPx = e.clientY - rect.top
  
  const x = (xPx / rect.width) * 100
  const y = (yPx / rect.height) * 100
  
  // Calculate default size as percentage relative to the page container
  // Default: 150px wide, 60px high (approx)
  // We convert this to % so it scales with the PDF page
  const widthPct = (150 / rect.width) * 100
  const heightPct = (60 / rect.height) * 100
  
  // Create new signature placement
  const newSignature = {
    id: signatureIdCounter++,
    pageUid: pageUid,
    image: signatureImage.value,
    x: Math.max(0, Math.min(x, 100)), // Allow full range 0-100
    y: Math.max(0, Math.min(y, 100)),
    width: widthPct,  // Store as %
    height: heightPct // Store as %
  }
  
  placedSignatures.value.push(newSignature)
  
  // Reset drag over state
  const page = pages.value.find(p => p.uid === pageUid)
  if (page) page.isDragOver = false
  
  // Reset flag after a short delay
  setTimeout(() => {
    isDropping = false
  }, 100)
}

function getSignaturesForPage(pageUid) {
  return placedSignatures.value.filter(sig => sig.pageUid === pageUid)
}

function deleteSignature(signatureId) {
  const index = placedSignatures.value.findIndex(sig => sig.id === signatureId)
  if (index !== -1) {
    placedSignatures.value.splice(index, 1)
  }
}

// Move signature
function startDragSignature(e, signature) {
  e.stopPropagation()
  currentDragSignature = signature
  isDraggingSignature.value = true
  dragStartX = e.clientX
  dragStartY = e.clientY
  
  // We need to know container dimensions for precise pixel->% drag if we wanted pixel-perfect drag
  // But current implementation uses a fixed sensitivity or direct mapping
  // Let's improve onDragSignature to be container-aware if possible, 
  // but for now the existing logic (delta * sensitivity) is a bit loose.
  // Ideally: deltaPct = (deltaPx / rect.width) * 100
  
  // Allow grabbing container from the DOM for context
  const el = e.target.closest('.relative') // The signature element's parent container
  if (el) {
    const rect = el.getBoundingClientRect()
    currentDragContainerWidth = rect.width
    currentDragContainerHeight = rect.height
  }
  
  document.addEventListener('mousemove', onDragSignature)
  document.addEventListener('mouseup', stopDragSignature)
}

let currentDragContainerWidth = 1000 // fallback
let currentDragContainerHeight = 1000 // fallback

function onDragSignature(e) {
  if (!currentDragSignature || !isDraggingSignature.value) return
  
  const deltaX = e.clientX - dragStartX
  const deltaY = e.clientY - dragStartY
  
  // Convert pixels to % based on container size
  const deltaXPct = (deltaX / currentDragContainerWidth) * 100
  const deltaYPct = (deltaY / currentDragContainerHeight) * 100
  
  currentDragSignature.x += deltaXPct
  currentDragSignature.y += deltaYPct
  
  // Keep within bounds (0-100 mostly)
  currentDragSignature.x = Math.max(0, Math.min(currentDragSignature.x, 99))
  currentDragSignature.y = Math.max(0, Math.min(currentDragSignature.y, 99))
  
  dragStartX = e.clientX
  dragStartY = e.clientY
}

function stopDragSignature() {
  isDraggingSignature.value = false
  currentDragSignature = null
  document.removeEventListener('mousemove', onDragSignature)
  document.removeEventListener('mouseup', stopDragSignature)
}

// Resize signature
let resizeStartWidthPct = 0
let resizeStartHeightPct = 0

function startResize(e, signature) {
  e.stopPropagation()
  currentDragSignature = signature
  isResizing.value = true
  dragStartX = e.clientX
  dragStartY = e.clientY
  
  resizeStartWidthPct = signature.width
  resizeStartHeightPct = signature.height
  
  // Get container dimensions to calculate % delta
  // The handle is inside the signature div, which is inside the container div
  const sigDiv = e.target.parentElement 
  const container = sigDiv.parentElement
  if (container) {
    const rect = container.getBoundingClientRect()
    currentDragContainerWidth = rect.width
    currentDragContainerHeight = rect.height
  }
  
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
}

function onResize(e) {
  if (!currentDragSignature || !isResizing.value) return
  
  const deltaX = e.clientX - dragStartX
  
  // Calculate delta %
  const deltaXPct = (deltaX / currentDragContainerWidth) * 100
  
  // Update width %
  const newWidthPct = Math.max(5, resizeStartWidthPct + deltaXPct) // Min 5% width
  
  // Maintain aspect ratio
  // ratio = height% / width% ?? No, ratio is visual.
  // Visual Ratio = (HeightPx / WidthPx). 
  // Height% = (HeightPx / ContainerHeight) * 100
  // Width% = (WidthPx / ContainerWidth) * 100
  // So Height% = (WidthPx * Ratio / ContainerHeight) * 100
  //             = ((Width% * ContainerWidth / 100) * Ratio / ContainerHeight) * 100
  //             = Width% * Ratio * (ContainerWidth / ContainerHeight)
  
  // Simplified: preserve the INITIAL ratio of percentages
  // IF the container aspect ratio doesn't change (which it behaves like a scaled image), 
  // then maintaining the ratio of % is correct.
  const ratioPct = resizeStartHeightPct / resizeStartWidthPct
  
  currentDragSignature.width = newWidthPct
  currentDragSignature.height = newWidthPct * ratioPct
}

function stopResize() {
  isResizing.value = false
  currentDragSignature = null
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
}

onMounted(() => {
  window.addEventListener('paste', handlePaste)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
})
</script>

<style scoped>
/* Scrollbar for Webkit browsers (Chrome, Edge, Safari) */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;               /* Scrollbar width */
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;      /* Track color */
  border-radius: 6px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #2ecc71; /* Thumb color */
  border-radius: 6px;
  border: 2px solid #f1f1f1; /* Optional: padding effect */
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #24bd64;
}

/* Firefox */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #2ecc71 #f1f1f1;
}
/* Add smooth transitions */
.group {
  @apply cursor-not-allowed;
}

.group:hover {
  @apply cursor-not-allowed;
}
</style>
